import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { sortBy } from 'ramda'
import {
  Box,
  Card,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import CalendarIcon from '@mui/icons-material/CalendarMonth'
import ListIcon from '@mui/icons-material/ViewList'
import { EventCalendar } from '@mui/x-scheduler/event-calendar'
import type { SchedulerEvent, SchedulerResource } from '@mui/x-scheduler/models'

import { useAppSelector } from 'src/store/hooks'
import {
  InstructorUser,
  selectFOAMfratInstructors,
  selectInstructorSessions,
} from 'src/v3/content-pipeline/instructor.slice'
import { selectEvents } from 'src/v3/content-pipeline/event-pipeline.slice'
import SessionListView from './components/session-list-view'
import ViewingScheduleAsDropdown from './viewing-schedule-as-dropdown'

dayjs.extend(utc)
dayjs.extend(timezone)

type Props = {
  isComplianceAdmin: boolean
  myInstructorLink: InstructorUser | null
}

export default function InstructorViewTab({
  isComplianceAdmin,
  myInstructorLink,
}: Props): ReactElement {
  let instructors = useAppSelector(selectFOAMfratInstructors)
  let sessions = useAppSelector(selectInstructorSessions)
  let events = useAppSelector(selectEvents)
  let userTimeZone = dayjs.tz.guess()

  let sortedInstructors = useMemo(
    () => sortBy((i) => i.name, instructors),
    [instructors],
  )

  let [selectedInstructorID, setSelectedInstructorID] = useState('')
  let [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar')

  useEffect(() => {
    if (!selectedInstructorID && sortedInstructors.length > 0) {
      setSelectedInstructorID(sortedInstructors[0].id)
    }
  }, [sortedInstructors, selectedInstructorID])

  let relevantInstructorID = isComplianceAdmin
    ? selectedInstructorID
    : (myInstructorLink?.instructorID ?? '')

  let relevantInstructor = instructors.find(
    (i) => i.id === relevantInstructorID,
  )

  let mySessions = useMemo(
    () =>
      sortBy(
        (s) => dayjs.utc(s.sessionTime).valueOf(),
        sessions.filter(
          (s) =>
            relevantInstructorID &&
            s.instructorIDs.includes(relevantInstructorID) &&
            dayjs.utc(s.sessionTime).isAfter(dayjs.utc()),
        ),
      ),
    [sessions, relevantInstructorID],
  )

  let nextSession = mySessions[0]
  let nextSessionEvent = events.find((e) => e.id === nextSession?.eventID)

  let resources = useMemo(
    (): SchedulerResource[] =>
      relevantInstructorID
        ? [
            {
              id: relevantInstructorID,
              title: relevantInstructor?.name ?? '',
            },
          ]
        : [],
    [relevantInstructorID, relevantInstructor],
  )

  let schedulerEvents = useMemo(
    (): SchedulerEvent[] =>
      mySessions.map((session) => {
        let event = events.find((e) => e.id === session.eventID)
        return {
          id: session.id,
          title: event?.name ?? 'Session',
          start: session.sessionTime,
          end:
            session.endTime ??
            dayjs
              .utc(session.sessionTime)
              .add(event?.hours ?? 1, 'hour')
              .toISOString(),
          resource: relevantInstructorID,
          readOnly: true,
        }
      }),
    [mySessions, events, relevantInstructorID],
  )

  if (!isComplianceAdmin && !myInstructorLink) {
    return (
      <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Your account isn't linked to an instructor profile yet. Contact an
          admin to get access to your schedule.
        </Typography>
      </Card>
    )
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" component="h2" sx={{ mb: 0.5 }}>
            My schedule
          </Typography>
          {isComplianceAdmin && (
            <Typography color="text.secondary" variant="body2">
              Admin preview — instructors land on this simplified view for their
              own account automatically.
            </Typography>
          )}
        </Box>
        {isComplianceAdmin && (
          <ViewingScheduleAsDropdown
            instructors={sortedInstructors}
            selectedID={selectedInstructorID}
            onChange={setSelectedInstructorID}
          />
        )}
      </Box>

      {nextSession ? (
        <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography
            color="primary"
            variant="caption"
            sx={{ fontWeight: 700, letterSpacing: 0.5 }}
          >
            NEXT SESSION
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {nextSessionEvent?.name ?? 'Session'}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {dayjs
              .utc(nextSession.sessionTime)
              .tz(userTimeZone)
              .format('ddd, MMM D · h:mm A')}
            {nextSession.endTime &&
              ` – ${dayjs.utc(nextSession.endTime).tz(userTimeZone).format('h:mm A')}`}
          </Typography>
        </Card>
      ) : (
        <Card variant="outlined" sx={{ p: 2, mb: 3, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">
            No upcoming sessions
          </Typography>
        </Card>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" component="h3">
          Upcoming sessions
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={viewMode}
          exclusive
          onChange={(_, value) => value && setViewMode(value)}
        >
          <ToggleButton value="calendar" sx={{ px: 1, width: 115 }}>
            <CalendarIcon sx={{ mr: 0.5, height: 18, width: 18 }} />
            <span>Calendar</span>
          </ToggleButton>
          <ToggleButton value="list" sx={{ px: 1, width: 115 }}>
            <ListIcon sx={{ mr: 0.5, height: 18, width: 18 }} />
            <span>List</span>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewMode === 'calendar' ? (
        <Box
          sx={{
            height: 600,
            '& .MuiEventCalendar-eventDialogPaper': {
              bgcolor: 'background.default',
            },
          }}
        >
          <EventCalendar
            events={schedulerEvents}
            resources={resources}
            visibleResources={{}}
            onEventsChange={() => {}}
            defaultVisibleDate={new Date()}
            defaultView="month"
            views={['month', 'week', 'day', 'agenda']}
            readOnly
            eventCreation={false}
          />
        </Box>
      ) : (
        <SessionListView
          sessions={mySessions}
          events={events}
          instructors={instructors}
          userTimeZone={userTimeZone}
        />
      )}
    </Box>
  )
}
